import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Paginação de tabela reutilizável: faixa "Mostrando X–Y de Z", seletor de itens
// por página e navegação (primeira/anterior/próxima/última).

export const PAGE_SIZES = [25, 50, 100, 200, 500];

interface Props {
    total: number;
    pagina: number;                     // 0-based
    pageSize: number;
    onPagina: (p: number) => void;
    onPageSize: (n: number) => void;
    /** Palavra usada na faixa ("Mostrando 1–50 de 200 consultas"). */
    label?: string;
    className?: string;
}

const TablePagination: React.FC<Props> = ({ total, pagina, pageSize, onPagina, onPageSize, label, className = '' }) => {
    const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
    const atual = Math.min(pagina, totalPaginas - 1);
    const inicio = atual * pageSize;
    const btn = 'px-2.5 py-1.5 text-xs font-bold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors';

    return (
        <div className={`flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-slate-100 dark:border-slate-800 ${className}`}>
            <span className="text-xs text-slate-500 dark:text-slate-400">
                {total === 0 ? 'Nenhum resultado' : (
                    <>Mostrando <b className="text-slate-700 dark:text-slate-200">{inicio + 1}–{Math.min(inicio + pageSize, total)}</b> de{' '}
                        <b className="text-slate-700 dark:text-slate-200">{total.toLocaleString('pt-BR')}</b>{label ? ` ${label}` : ''}</>
                )}
            </span>

            <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 ml-2">
                Por página:
                <select
                    value={pageSize}
                    onChange={(e) => { onPageSize(Number(e.target.value)); onPagina(0); }}
                    className="text-xs font-bold px-2 py-1 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                >
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
            </label>

            <div className="flex items-center gap-1.5 ml-auto">
                <button onClick={() => onPagina(0)} disabled={atual === 0} className={btn} title="Primeira página">«</button>
                <button onClick={() => onPagina(Math.max(0, atual - 1))} disabled={atual === 0} className={`${btn} flex items-center gap-1`}>
                    <ChevronLeft size={13} /> Anterior
                </button>
                <span className="text-xs font-bold text-slate-700 dark:text-slate-200 px-1.5">
                    {atual + 1}<span className="text-slate-400 font-normal"> / {totalPaginas}</span>
                </span>
                <button onClick={() => onPagina(Math.min(totalPaginas - 1, atual + 1))} disabled={atual >= totalPaginas - 1} className={`${btn} flex items-center gap-1`}>
                    Próxima <ChevronRight size={13} />
                </button>
                <button onClick={() => onPagina(totalPaginas - 1)} disabled={atual >= totalPaginas - 1} className={btn} title="Última página">»</button>
            </div>
        </div>
    );
};

export default TablePagination;
